import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  ShadcnSelect,
  ShadcnSelectContent,
  ShadcnSelectItem,
  ShadcnSelectTrigger,
  ShadcnSelectValue,
} from '@aaas/shared';
import { ROUND_TYPE_OPTIONS } from '../types';
import { useMemo } from 'react';

export interface RoundFormData {
  id: string;
  name: string;
  round_type: string;
  isDefault?: boolean;
}

interface RoundFormItemProps {
  round: RoundFormData;
  index: number;
  onUpdate: (id: string, field: 'name' | 'round_type', value: string) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

export function RoundFormItem({
  round,
  index,
  onUpdate,
  onDelete,
  disabled = false,
}: RoundFormItemProps) {
  const { t } = useTranslation('ats');
  const getRoundTypeLabel = (roundType: string) => {
    const translated = t(`pipeline.roundTypes.${roundType}`, { defaultValue: '' });
    return translated || roundType;
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: round.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const availableRoundTypes = useMemo(() => {
    return round.isDefault ? ROUND_TYPE_OPTIONS : ROUND_TYPE_OPTIONS.filter((option) => option.value !== 'application_received' && option.value !== 'hired');
  }, [round.isDefault]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg
        ${isDragging ? 'opacity-50 shadow-lg z-50' : ''}
        ${disabled ? 'opacity-50' : ''}
      `}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className={`p-1 focus:outline-none ${
          disabled || round.isDefault
            ? 'cursor-not-allowed text-gray-300'
            : 'cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600'
        }`}
        disabled={disabled || round.isDefault}
        type="button"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Order Number */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
        round.isDefault ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
      }`}>
        {index + 1}
      </div>

      {/* Form Fields */}
      <div className="flex-grow grid grid-cols-2 gap-3">
        <div>
          <Input
            id={`round-name-${round.id}`}
            value={round.name}
            onChange={(e) => onUpdate(round.id, 'name', e.target.value)}
            placeholder={t('pipeline.roundDialog.namePlaceholder')}
            disabled={disabled}
          />
        </div>

        <div>
          <ShadcnSelect
            value={round.round_type}
            onValueChange={(value: string) => onUpdate(round.id, 'round_type', value)}
            disabled={disabled || round.isDefault}
          >
            <ShadcnSelectTrigger className="w-full !h-10 rounded-md border-gray-300 focus-visible:ring-2 focus-visible:ring-blue-500">
              <ShadcnSelectValue placeholder={t('pipeline.roundItem.selectType')} />
            </ShadcnSelectTrigger>
            <ShadcnSelectContent position="popper" sideOffset={4} className="max-h-60 z-50 min-w-[var(--radix-select-trigger-width)] rounded-md border bg-popover shadow-md">
              {availableRoundTypes.map((option) => (
                <ShadcnSelectItem
                  key={option.value}
                  value={option.value}
                  className="h-8 cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  {getRoundTypeLabel(option.value)}
                </ShadcnSelectItem>
              ))}
            </ShadcnSelectContent>
          </ShadcnSelect>
        </div>
      </div>

      {/* Delete Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(round.id)}
        disabled={disabled || round.isDefault}
        className="h-8 w-8 text-gray-500 hover:text-red-600 flex-shrink-0"
        type="button"
        title={round.isDefault ? t('pipeline.roundItem.deleteDefaultTitle') : t('pipeline.roundItem.deleteTitle')}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
